import { rgbaCss } from "./color";
import { computeFreePos, computePos } from "./geometry";
import type { Settings } from "./types";

export type Ctx2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** 水印内容块的布局结果（绘制与拖拽命中测试共用） */
export interface WatermarkLayout {
  /** 内容块外框（含背景框内边距）在图片坐标系里的位置 */
  box: Box;
  pad: number;
  contentW: number;
  contentH: number;
  logoW: number;
  logoH: number;
  gap: number;
  textW: number;
  textH: number;
  ascent: number;
  fontPx: number;
  strokePx: number;
}

function roundRect(ctx: Ctx2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, w, h, radius);
    return;
  }
  // 兜底：手动画圆角路径
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.arcTo(x + w, y, x + w, y + radius, radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
  ctx.lineTo(x + radius, y + h);
  ctx.arcTo(x, y + h, x, y + h - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
}

/**
 * 计算水印块的尺寸与位置（会临时改 ctx.font，用于量文字）。
 * 没有内容（既无文字也无 LOGO）时返回 null。
 */
export function computeLayout(
  ctx: Ctx2D,
  W: number,
  H: number,
  st: Settings,
  logo: { width: number; height: number } | null,
): WatermarkLayout | null {
  if (!st.text && !logo) return null;

  const fontPx = Math.max(1, Math.round(H * st.fontSizeRatio));
  ctx.font = `${fontPx}px "${st.fontFamily}", sans-serif`;

  let textW = 0;
  let ascent = 0;
  let textH = 0;
  if (st.text) {
    const metrics = ctx.measureText(st.text);
    ascent = metrics.actualBoundingBoxAscent || fontPx * 0.8;
    const descent = metrics.actualBoundingBoxDescent || fontPx * 0.25;
    textW = metrics.width;
    textH = ascent + descent;
  }

  const pad = Math.max(0, Math.round(H * st.boxPaddingRatio));
  const gap = logo && st.text ? Math.max(1, Math.round(pad * 0.6)) : 0;
  let logoW = 0;
  let logoH = 0;
  if (logo) {
    logoH = Math.max(1, Math.round(H * st.logoScaleRatio));
    logoW = Math.max(1, Math.round((logoH * logo.width) / logo.height));
  }

  const contentW = logoW + gap + textW;
  const contentH = Math.max(logoH, textH);
  if (contentW <= 0 || contentH <= 0) return null;

  const boxW = contentW + pad * 2;
  const boxH = contentH + pad * 2;
  const [x, y] =
    st.positionMode === "free"
      ? computeFreePos(st.freeX, st.freeY, boxW, boxH, W, H)
      : computePos(st.anchor, boxW, boxH, W, H, st.offsetX * W, st.offsetY * H);

  return {
    box: { x, y, w: boxW, h: boxH },
    pad,
    contentW,
    contentH,
    logoW,
    logoH,
    gap,
    textW,
    textH,
    ascent,
    fontPx,
    strokePx: st.strokeWidthRatio > 0 ? Math.max(1, Math.round(H * st.strokeWidthRatio)) : 0,
  };
}

/**
 * 在已按方向摆正的画布上绘制水印。
 * 顺序：遮挡色块 → （单块：背景框 + LOGO/文字；平铺：LOGO/文字网格）
 */
export function drawWatermark(
  ctx: Ctx2D,
  W: number,
  H: number,
  st: Settings,
  logo: ImageBitmap | null,
): void {
  // 1) 遮挡色块（用比例坐标盖住原图上的旧水印）
  if (st.maskEnabled && st.maskColor[3] > 0) {
    const [x0, y0, x1, y1] = st.maskRect;
    ctx.save();
    ctx.fillStyle = rgbaCss(st.maskColor);
    ctx.fillRect(
      Math.round(Math.min(x0, x1) * W),
      Math.round(Math.min(y0, y1) * H),
      Math.round(Math.abs(x1 - x0) * W),
      Math.round(Math.abs(y1 - y0) * H),
    );
    ctx.restore();
  }

  const layout = computeLayout(ctx, W, H, st, logo);
  if (!layout) return;

  ctx.save();
  ctx.font = `${layout.fontPx}px "${st.fontFamily}", sans-serif`;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";

  /** 以 (x, y) 为左上角画一份内容块（LOGO + 文字） */
  const drawBlock = (x: number, y: number): void => {
    let cursor = x;
    if (logo) {
      ctx.globalAlpha = Math.max(0, Math.min(1, st.logoOpacity / 255));
      ctx.drawImage(logo, cursor, y + (layout.contentH - layout.logoH) / 2, layout.logoW, layout.logoH);
      ctx.globalAlpha = 1;
      cursor += layout.logoW + layout.gap;
    }
    if (st.text) {
      const ty = y + (layout.contentH - layout.textH) / 2 + layout.ascent;
      if (layout.strokePx > 0 && st.strokeColor[3] > 0) {
        ctx.lineWidth = layout.strokePx;
        ctx.lineJoin = "round";
        ctx.miterLimit = 2;
        ctx.strokeStyle = rgbaCss(st.strokeColor);
        ctx.strokeText(st.text, cursor, ty);
      }
      ctx.fillStyle = rgbaCss(st.textColor);
      ctx.fillText(st.text, cursor, ty);
    }
  };

  if (st.tileEnabled) {
    // 2) 平铺：绕画面中心旋转后按网格铺满（平铺模式不画背景框，位置参数不生效）
    const step = Math.max(layout.contentW, layout.contentH, Math.round(H * st.tileGapRatio));
    const stepX = layout.contentW + step;
    const stepY = layout.contentH + step;
    const diag = Math.hypot(W, H);
    const cols = Math.ceil(diag / stepX) + 2;
    const rows = Math.ceil(diag / stepY) + 2;

    ctx.translate(W / 2, H / 2);
    ctx.rotate((st.tileAngle * Math.PI) / 180);
    for (let row = 0; row < rows; row++) {
      const y = -diag / 2 + row * stepY;
      // 隔行错开半个步长，避免出现明显的竖沟
      const offsetX = row % 2 === 0 ? 0 : stepX / 2;
      for (let col = 0; col < cols; col++) {
        drawBlock(-diag / 2 + col * stepX + offsetX, y);
      }
    }
  } else {
    // 3) 单块：先画背景框，再画内容
    if (st.boxColor[3] > 0) {
      ctx.fillStyle = rgbaCss(st.boxColor);
      roundRect(ctx, layout.box.x, layout.box.y, layout.box.w, layout.box.h, Math.min(layout.box.w, layout.box.h) * st.boxRadiusRatio);
      ctx.fill();
    }
    drawBlock(layout.box.x + layout.pad, layout.box.y + layout.pad);
  }

  ctx.restore();
}
